import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Ruler, Factory, PackageCheck, Boxes, Layers, Truck } from "lucide-react";
import SEO from "@/components/SEO";
import Overline from "@/components/Overline";
import ProductCard from "@/components/ProductCard";
import ClientMarquee from "@/components/ClientMarquee";
import { products, categories, activeProducts } from "@/data/products";
import { company, seo, buildQuoteUrl } from "@/config/site";

const CATEGORY_ICONS = {
  corrugated: Boxes,
  pp: Layers,
  protective: ShieldCheck,
  accessories: PackageCheck,
  pallets: Truck,
};

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1587293852726-70cdb56c2866?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxjb3JydWdhdGVkJTIwY2FyZGJvYXJkJTIwYm94ZXMlMjB3YXJlaG91c2V8ZW58MHx8fHwxNzg4MDgyMTc1fDA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const featured = products.filter((p) => p.featured && p.active !== false);
  const productCount = activeProducts().length;

  const orgLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: company.fullCompanyName,
    url: seo.siteUrl,
    logo: `${seo.siteUrl}${company.logo.src}`,
    telephone: company.phone,
    email: company.email || undefined,
    foundingDate: String(company.establishedYear),
    address: {
      "@type": "PostalAddress",
      streetAddress: company.address.line1,
      addressLocality: "Greater Noida",
      addressRegion: company.address.region,
      postalCode: company.address.postalCode,
      addressCountry: company.address.countryCode,
    },
  };

  return (
    <>
      <SEO
        title="Industrial Packaging Solutions"
        description={seo.defaultDescription}
        canonical={`${seo.siteUrl}/`}
        keywords={["industrial packaging", "corrugated boxes", "pp bins", "greater noida", "rudraksh"]}
        jsonLd={orgLd}
      />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-neutral-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center py-16 md:py-24">
          <div className="lg:col-span-7">
            <Overline>Industrial Packaging · Est. {company.establishedYear}</Overline>
            <h1 className="font-heading mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-neutral-900 leading-[1.05]">
              Packaging solutions built around <span className="text-[#A91D22]">your requirements.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-neutral-600 leading-relaxed max-w-2xl">
              From corrugated packaging and PP solutions to protective packaging materials and industrial handling products, {company.fullCompanyName} provides packaging products suited to your business requirements.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/products"
                className="inline-flex items-center gap-2 bg-neutral-900 text-white px-6 py-3 text-sm font-medium hover:bg-neutral-800 transition-colors rounded-sm"
                data-testid="hero-explore-products"
              >
                Explore Products <ArrowRight size={16} />
              </Link>
              <a
                href={buildQuoteUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#A91D22] text-white px-6 py-3 text-sm font-medium hover:bg-[#8B181C] transition-colors rounded-sm"
                data-testid="hero-request-quote"
              >
                Request a Quote <ArrowRight size={16} />
              </a>
            </div>

            <dl className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
              <div>
                <dt className="text-[11px] uppercase tracking-[0.18em] text-neutral-500">Since</dt>
                <dd className="mt-1 font-heading text-2xl font-semibold text-neutral-900">{company.establishedYear}</dd>
              </div>
              <div>
                <dt className="text-[11px] uppercase tracking-[0.18em] text-neutral-500">Categories</dt>
                <dd className="mt-1 font-heading text-2xl font-semibold text-neutral-900">{categories.length}</dd>
              </div>
              <div>
                <dt className="text-[11px] uppercase tracking-[0.18em] text-neutral-500">Products</dt>
                <dd className="mt-1 font-heading text-2xl font-semibold text-neutral-900">{productCount}</dd>
              </div>
            </dl>
          </div>

          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] w-full overflow-hidden border border-neutral-200">
              <img
                src={HERO_IMAGE}
                alt="Warehouse packaging operations"
                className="absolute inset-0 h-full w-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/60 via-neutral-900/10 to-transparent" aria-hidden="true" />
              <div className="absolute bottom-5 left-5 right-5 text-white">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/80">Greater Noida, India</div>
                <div className="mt-1 font-heading text-lg font-semibold">Industrial-grade packaging</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMPANY INTRO */}
      <section className="py-16 md:py-24 bg-[#FAFAFA] border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-4">
            <Overline>The Company</Overline>
            <h2 className="font-heading mt-4 text-3xl sm:text-4xl font-semibold tracking-tight text-neutral-900">
              {company.fullCompanyName}
            </h2>
          </div>
          <div className="md:col-span-8 space-y-4 text-neutral-600 leading-relaxed">
            <p>
              Established in {company.establishedYear} and based in Greater Noida, Uttar Pradesh, {company.fullCompanyName} supplies packaging products across corrugated, PP and protective packaging categories, alongside pallets, accessories and packaging materials.
            </p>
            <p>
              We work around your product, application and dispatch requirements to identify the appropriate packaging — including customer-specified dimensions and quantities.
            </p>
            <div className="pt-4">
              <Link
                to="/about"
                className="text-sm font-medium text-[#A91D22] inline-flex items-center gap-1 hover:text-[#8B181C]"
                data-testid="home-about-link"
              >
                About the company <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCT CATEGORIES */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <Overline>Product Range</Overline>
              <h2 className="font-heading mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-neutral-900">
                Packaging categories
              </h2>
            </div>
            <Link
              to="/products"
              className="text-sm font-medium text-neutral-900 inline-flex items-center gap-1 hover:text-[#A91D22]"
              data-testid="home-view-all-products"
            >
              View all products <ArrowRight size={14} />
            </Link>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((cat) => {
              const Icon = CATEGORY_ICONS[cat.id] || Boxes;
              return (
                <div
                  key={cat.id}
                  className="bg-white border border-neutral-200 p-6 flex flex-col rounded-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
                  data-testid={`category-card-${cat.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#FEE2E2] flex items-center justify-center rounded-sm">
                      <Icon size={20} className="text-[#A91D22]" aria-hidden="true" />
                    </div>
                    <h3 className="font-heading text-lg font-semibold tracking-tight text-neutral-900">{cat.name}</h3>
                  </div>
                  <p className="mt-3 text-sm text-neutral-600 leading-relaxed">{cat.description}</p>
                  <ul className="mt-4 space-y-1.5 text-sm">
                    {cat.slugs.map((slug) => {
                      const p = products.find((x) => x.slug === slug);
                      return (
                        <li key={slug}>
                          <Link to={`/products/${slug}`} className="text-neutral-700 hover:text-[#A91D22]">
                            {p?.name}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CUSTOMIZATION */}
      <section className="py-16 md:py-24 bg-neutral-900 text-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#F87171]">Customization</div>
            <h2 className="font-heading mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white">
              Your requirement. Your dimensions. Your quantity.
            </h2>
            <p className="mt-5 text-neutral-400 leading-relaxed max-w-2xl">
              Packaging requirements differ from product to product. Discuss your dimensions, quantities and application with {company.fullCompanyName}.
            </p>
            <a
              href={buildQuoteUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center gap-2 bg-[#A91D22] text-white px-6 py-3 text-sm font-medium hover:bg-[#8B181C] rounded-sm"
              data-testid="home-discuss-requirement"
            >
              Request a Quote <ArrowRight size={16} />
            </a>
          </div>
          <div className="lg:col-span-5">
            <ul className="space-y-4">
              {[
                { icon: Ruler, title: "Dimensions to specification", body: "Sizes worked around the product being packed." },
                { icon: Factory, title: "Requirement-based quantities", body: "Volumes discussed based on your usage pattern." },
                { icon: PackageCheck, title: "Application-fit selection", body: "Packaging matched to storage and dispatch needs." },
              ].map((f) => (
                <li key={f.title} className="border border-neutral-800 bg-neutral-950 p-5 flex gap-4">
                  <f.icon size={22} className="text-[#F87171] mt-0.5" aria-hidden="true" />
                  <div>
                    <div className="text-white font-medium">{f.title}</div>
                    <div className="text-sm text-neutral-400 mt-1">{f.body}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between gap-4">
            <div>
              <Overline>Selected products</Overline>
              <h2 className="font-heading mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-neutral-900">Featured packaging products</h2>
            </div>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((p) => (
              <ProductCard key={p.slug} product={p} />
            ))}
          </div>
        </div>
      </section>

      {/* CLIENT MARQUEE */}
      <section className="py-16 md:py-20 bg-[#F5F5F5] border-y border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <Overline>Trusted Relationships</Overline>
            <h2 className="font-heading mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-neutral-900">
              Working with businesses across sectors
            </h2>
            <p className="mt-3 text-neutral-600">
              A selection of companies we have supplied packaging to.
            </p>
          </div>
          <div className="mt-10">
            <ClientMarquee />
          </div>
          <div className="mt-8 text-center">
            <Link to="/clients" className="text-sm font-medium text-neutral-900 hover:text-[#A91D22] inline-flex items-center gap-1">
              View all clients <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-16 md:py-24 bg-white border-t border-neutral-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Overline className="!text-neutral-500">Get in touch</Overline>
          <h2 className="font-heading mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-neutral-900">
            Have a packaging requirement?
          </h2>
          <p className="mt-5 text-neutral-600 leading-relaxed">
            Share your requirement with us and our team can discuss the appropriate packaging solution.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a
              href={buildQuoteUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#A91D22] text-white px-6 py-3 text-sm font-medium hover:bg-[#8B181C] rounded-sm"
              data-testid="home-final-cta-quote"
            >
              Request a Quote <ArrowRight size={16} />
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 border border-neutral-300 text-neutral-900 px-6 py-3 text-sm font-medium hover:bg-neutral-50 rounded-sm"
            >
              Contact us
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
